import {Component, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MediaMatcher} from '@angular/cdk/layout';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import * as _ from 'underscore';
import {environment} from 'src/environments/environment';
import {AdminService} from 'src/app/services/admin.service';
import {SortService} from 'src/app/services/sort.service';
import {SnackbarService} from 'src/app/services/snackbar.service';
import {ConfirmComponent} from 'src/app/components/common/bottom-sheet/confirm/confirm.component';
import {ObjecttypeCategory, ObjecttypeGroup, ObjecttypeSubgroup} from 'src/app/models/Objecttype';

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.css'],
})
export class CategoriesComponent implements OnInit {
  isLoading = true;
  mobileQuery: MediaQueryList;
  categories: ObjecttypeCategory[] = [];
  groups: ObjecttypeGroup[] = [];
  subgroups: ObjecttypeSubgroup[] = [];
  // id of the item currently being renamed (category/group/subgroup share the namespace via ids)
  editingId: string = null;
  private ngUnsubscribe = new Subject();

  constructor(
    private adminservice: AdminService,
    private sortservice: SortService,
    private snackbarservice: SnackbarService,
    private bottomSheet: MatBottomSheet,
    media: MediaMatcher,
  ) {
    this.mobileQuery = media.matchMedia(environment.mobileQueryMaxwidthSmall);
  }

  ngOnInit(): void {
    this.adminservice.getCategories()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((categories) => {
        this.categories = _.reject(categories, _.isNull).sort(this.sortservice.dynamicSort('name'));
        this.isLoading = false;
      });
    this.adminservice.getGroups()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((groups) => {
        this.groups = _.reject(groups, _.isNull).sort(this.sortservice.dynamicSort('name'));
      });
    this.adminservice.getSubgroups()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((subgroups) => {
        this.subgroups = _.reject(subgroups, _.isNull).sort(this.sortservice.dynamicSort('name'));
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  groupsFor(categoryId: string): ObjecttypeGroup[] {
    return this.groups.filter((g) => g.categoryid === categoryId);
  }

  subgroupsFor(groupId: string): ObjecttypeSubgroup[] {
    return this.subgroups.filter((s) => s.groupid === groupId);
  }

  toggleEdit(id: string) {
    this.editingId = this.editingId === id ? null : id;
  }

  // ----- Kategori -----
  async addCategory(name: string) {
    if (!name || name.trim().length === 0) {
      return;
    }
    await this.adminservice.createCategory({name: name.trim()});
    this.snackbarservice.openSnackBar('Kategori opprettet: ' + name.trim(), '');
  }

  async renameCategory(category: ObjecttypeCategory, name: string) {
    if (!name || name.trim().length === 0) {
      return;
    }
    await this.adminservice.updateCategory({id: category.id, name: name.trim()});
    this.editingId = null;
  }

  deleteCategory(category: ObjecttypeCategory) {
    this.confirmDelete(
      'Slette kategori: ' + category.name,
      'Grupper og undergrupper under kategorien blir ikke slettet automatisk.',
      async () => {
        await this.adminservice.deleteCategory(category.id);
        this.snackbarservice.openSnackBar('Kategori slettet', '');
      });
  }

  // ----- Gruppe -----
  async addGroup(categoryId: string, name: string) {
    if (!name || name.trim().length === 0) {
      return;
    }
    await this.adminservice.createGroup({name: name.trim(), categoryid: categoryId});
    this.snackbarservice.openSnackBar('Gruppe opprettet: ' + name.trim(), '');
  }

  async renameGroup(group: ObjecttypeGroup, name: string) {
    if (!name || name.trim().length === 0) {
      return;
    }
    await this.adminservice.updateGroup({id: group.id, name: name.trim(), categoryid: group.categoryid});
    this.editingId = null;
  }

  deleteGroup(group: ObjecttypeGroup) {
    this.confirmDelete(
      'Slette gruppe: ' + group.name,
      'Undergrupper under gruppen blir ikke slettet automatisk.',
      async () => {
        await this.adminservice.deleteGroup(group.id);
        this.snackbarservice.openSnackBar('Gruppe slettet', '');
      });
  }

  // ----- Undergruppe -----
  async addSubgroup(groupId: string, name: string) {
    if (!name || name.trim().length === 0) {
      return;
    }
    await this.adminservice.createSubgroup({name: name.trim(), groupid: groupId});
    this.snackbarservice.openSnackBar('Undergruppe opprettet: ' + name.trim(), '');
  }

  async renameSubgroup(subgroup: ObjecttypeSubgroup, name: string) {
    if (!name || name.trim().length === 0) {
      return;
    }
    await this.adminservice.updateSubgroup({id: subgroup.id, name: name.trim(), groupid: subgroup.groupid});
    this.editingId = null;
  }

  deleteSubgroup(subgroup: ObjecttypeSubgroup) {
    this.confirmDelete(
      'Slette undergruppe: ' + subgroup.name,
      '',
      async () => {
        await this.adminservice.deleteSubgroup(subgroup.id);
        this.snackbarservice.openSnackBar('Undergruppe slettet', '');
      });
  }

  private confirmDelete(title: string, text: string, onConfirm: () => Promise<void>) {
    const sheet = this.bottomSheet.open(ConfirmComponent, {
      backdropClass: 'blur-dialog-backdrop',
      data: {
        title: 'Er du sikker?',
        text: text && text.length > 0 ? title + '. ' + text : title,
        yes: {name: 'Ja', icon: 'delete'},
        no: {name: 'Nei', icon: 'close'},
      },
    });
    sheet.afterDismissed().pipe(takeUntil(this.ngUnsubscribe)).subscribe((confirm) => {
      if (confirm) {
        onConfirm().catch((err) => console.log(err));
      }
    });
  }
}
